import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth-context';
import { useLanguage } from '../contexts/language-context';
import Layout from './Layout';
import { MediaCard, MediaListItem } from './media-display';
import apiService from '../services/api-service';
import { CONSOLE_MESSAGES } from '../constants';
import { SPINNER_CLASSES } from '../styles';
import { useScrollRestoration } from '../hooks/use-scroll-restoration';

function MediaListPage({ 
  title, 
  subtitle, 
  icon, 
  emptyMessage, 
  getUserMediaItems, 
  removeFromUserList 
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { t } = useLanguage();

  const loadUserItems = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userItems = getUserMediaItems();
      
      if (userItems === undefined || userItems === null) {
        setLoading(false);
        return;
      }
      
      if (userItems.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }
      
      // Build a lookup map of addedAt timestamps keyed by "mediaType-itemId"
      const timestampMap = new Map();
      const movieIds = [];
      const tvIds = [];

      for (const item of userItems) {
        const key = `${item.mediaType}-${item.itemId}`;
        timestampMap.set(key, item.addedAt || item.watchedAt);
        if (item.mediaType === 'movie') movieIds.push(item.itemId);
        else tvIds.push(item.itemId);
      }

      // Single batch request for all items
      const batchData = await apiService.getMediaBatch(movieIds, tvIds);

      const allItems = [
        ...(batchData.movies || []).map(m => ({ ...m, media_type: 'movie' })),
        ...(batchData.tvShows || []).map(t => ({ ...t, media_type: 'tv' })),
      ].map(item => ({
        ...item,
        addedAt: timestampMap.get(`${item.media_type}-${item.id}`),
      }));

      allItems.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
      setItems(allItems);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_LOADING_USER_ITEMS, error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, getUserMediaItems]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setItems([]);
      setLoading(false);
      return;
    }

    if (!user) {
      setLoading(true);
      return;
    }

    loadUserItems();
  }, [isAuthenticated, user, authLoading, loadUserItems, getUserMediaItems]);

  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      const userItems = getUserMediaItems();
      
      if (userItems && (items.length === 0 || Math.abs(userItems.length - items.length) > 0)) {
        loadUserItems();
      }
    }
  }, [user?.watchlist, user?.watched, user?.favorites, isAuthenticated, authLoading, getUserMediaItems, items.length, loadUserItems, user]);

  const handleRemoveItem = async (itemId, mediaType) => {
    try {
      await removeFromUserList(itemId, mediaType);
      setItems(prev => prev.filter(item => !(item.id === itemId && item.media_type === mediaType)));
    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_REMOVING_ITEM, error);
    }
  };

  const movieItems = items.filter(item => item.media_type === 'movie');
  const tvItems = items.filter(item => item.media_type === 'tv');

  // Scroll restoration for navigating back from detail pages
  const locationKey = useScrollRestoration({
    mediaType: `userlist-${title}`,
    searchQuery: '',
    filters: {},
    isLoading: loading,
    itemsLength: items.length
  });

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <Layout>
        <div className="relative min-h-[calc(100vh-72px-64px)] bg-gradient-to-b from-dark-900 via-dark-800/70 to-dark-900 flex items-center justify-center">
          <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
        </div>
      </Layout>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="relative min-h-[calc(100vh-72px-64px)] bg-gradient-to-b from-dark-900 via-dark-800/70 to-dark-900 flex items-center">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5">
              {icon}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3">
              {t('createFreeAccount')}
            </h1>
            <p className="text-gray-400 text-sm sm:text-base mb-6 sm:mb-7 max-w-xl mx-auto">
              {t('loginOrRegisterPrompt')}
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-7 text-left max-w-3xl mx-auto">
              <div className="flex items-start gap-2.5 p-3 sm:p-4 bg-dark-800/50 border border-dark-700 rounded-lg">
                <div className="w-9 h-9 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm sm:text-base mb-0.5">{t('watchlistBenefit')}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm">{t('watchlistBenefitDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 p-3 sm:p-4 bg-dark-800/50 border border-dark-700 rounded-lg">
                <div className="w-9 h-9 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm sm:text-base mb-0.5">{t('watchedBenefit')}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm">{t('watchedBenefitDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 p-3 sm:p-4 bg-dark-800/50 border border-dark-700 rounded-lg">
                <div className="w-9 h-9 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm sm:text-base mb-0.5">{t('favoritesBenefit')}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm">{t('favoritesBenefitDesc')}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => {
                  const event = new CustomEvent('openAuthModal', { detail: { mode: 'register' } });
                  window.dispatchEvent(event);
                }}
                className="w-full sm:w-auto btn-primary px-8 py-2.5 sm:py-3 text-base font-semibold"
              >
                {t('registerFree')}
              </button>
              <button
                onClick={() => {
                  const event = new CustomEvent('openAuthModal', { detail: { mode: 'login' } });
                  window.dispatchEvent(event);
                }}
                className="w-full sm:w-auto px-8 py-2.5 sm:py-3 text-base font-semibold text-gray-300 hover:text-white bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
              >
                {t('alreadyHaveAccount')}
              </button>
            </div>

              <p className="text-gray-500 text-xs sm:text-sm mt-4">
                {t('registrationFast')}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        {/* Header */}
        <section className="py-6 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start sm:items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">{title}</h1>
              <p className="text-gray-400 mt-0.5 sm:mt-1 text-sm sm:text-base">{subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* View Mode Toggle */}
      {items.length > 0 && (
        <section className="sticky top-16 sm:top-[72px] lg:top-[73px] z-40 backdrop-blur-xl border-b border-dark-700/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-400">
                Iš viso: <span className="text-white font-medium">{items.length}</span>
                {movieItems.length > 0 && tvItems.length > 0 && (
                  <span> ({movieItems.length} filmai, {tvItems.length} TV serialai)</span>
                )}
              </p>

              <div className="flex bg-dark-700 rounded-lg p-1 flex-shrink-0">
                <button 
                  onClick={() => setViewMode('grid')}
                  className={`p-2 sm:p-2.5 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'text-primary-400 bg-dark-600' 
                      : 'text-gray-400 hover:text-white hover:bg-dark-600'
                  }`} 
                  title="Tinklelio vaizdas"
                  aria-label="Tinklelio vaizdas"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button 
                  onClick={() => setViewMode('list')}
                  className={`p-2 sm:p-2.5 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'text-primary-400 bg-dark-600' 
                      : 'text-gray-400 hover:text-white hover:bg-dark-600'
                  }`} 
                  title="Sąrašo vaizdas"
                  aria-label="Sąrašo vaizdas"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-dark-700 rounded-xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              {icon}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Tuščias sąrašas</h2>
            <p className="text-gray-400 text-base sm:text-lg mb-4 sm:mb-6 px-4">{emptyMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary px-5 py-2.5 sm:px-6 sm:py-3"
            >
              Tyrinėti filmus
            </button>
          </div>
        ) : (
          <div className="space-y-8 sm:space-y-10">
            {/* Movies Section */}
            {movieItems.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4 sm:mb-5">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">Filmai</h2>
                    <p className="text-xs sm:text-sm text-gray-400">{movieItems.length} {movieItems.length === 1 ? 'filmas' : movieItems.length < 10 ? 'filmai' : 'filmų'}</p>
                  </div>
                </div>
                <div className={viewMode === 'grid' ? 'grid-uniform' : 'list-uniform'}>
                  {movieItems.map((item) => (
                    <div key={`movie-${item.id}`} className="relative group">
                      {viewMode === 'grid' ? (
                        <MediaCard 
                          media={item} 
                          mediaType={item.media_type}
                          searchQuery=""
                          filters={{}}
                          locationKey={locationKey}
                        />
                      ) : (
                        <MediaListItem
                          media={item}
                          mediaType={item.media_type}
                          searchQuery=""
                          filters={{}}
                          locationKey={locationKey}
                        />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(item.id, item.media_type);
                        }}
                        className="absolute top-2 right-2 z-10 p-2 bg-black/70 hover:bg-red-600 text-gray-300 hover:text-white rounded-lg lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm shadow-lg active:scale-95"
                        title="Pašalinti"
                        aria-label="Pašalinti iš sąrašo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Divider between sections */}
            {movieItems.length > 0 && tvItems.length > 0 && (
              <div className="border-t border-dark-700/50" />
            )}

            {/* TV Shows Section */}
            {tvItems.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4 sm:mb-5">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">TV serialai</h2>
                    <p className="text-xs sm:text-sm text-gray-400">{tvItems.length} {tvItems.length === 1 ? 'serialas' : tvItems.length < 10 ? 'serialai' : 'serialų'}</p>
                  </div>
                </div>
                <div className={viewMode === 'grid' ? 'grid-uniform' : 'list-uniform'}>
                  {tvItems.map((item) => (
                    <div key={`tv-${item.id}`} className="relative group">
                      {viewMode === 'grid' ? (
                        <MediaCard 
                          media={item} 
                          mediaType={item.media_type}
                          searchQuery=""
                          filters={{}}
                          locationKey={locationKey}
                        />
                      ) : (
                        <MediaListItem
                          media={item}
                          mediaType={item.media_type}
                          searchQuery=""
                          filters={{}}
                          locationKey={locationKey}
                        />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(item.id, item.media_type);
                        }}
                        className="absolute top-2 right-2 z-10 p-2 bg-black/70 hover:bg-red-600 text-gray-300 hover:text-white rounded-lg lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-200 backdrop-blur-sm shadow-lg active:scale-95"
                        title="Pašalinti"
                        aria-label="Pašalinti iš sąrašo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      </div>
    </Layout>
  );
}

export default MediaListPage; 